import React from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowRight, ShieldCheck, Zap, Heart, Star, 
  ChevronRight, Facebook, Twitter, Instagram, Linkedin 
} from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();

  return (
    <main className="pt-20">
      {/* SECTION 1: HERO (Image 3 Top) */}
      <section className="relative bg-white overflow-hidden min-h-[600px] flex items-center">
        <div className="absolute top-0 right-0 w-[45%] h-full bg-[#FFC107] transform skew-x-[-15deg] translate-x-1/4 z-0" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-left py-12 md:py-20">
              <h1 className="text-5xl md:text-6xl font-black text-gray-900 leading-tight">
                Advance Your<br />Career,<br />
                <span className="text-[#FFC107]">Learn Something<br />New</span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-md leading-relaxed">
                Transform Your Life Through Education. Where students meet teachers around the world online.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <button 
                  onClick={() => navigate("/courses")} 
                  className="bg-[#FFC107] text-gray-900 font-bold px-8 py-4 rounded-lg flex items-center gap-2 hover:bg-[#EBB000] transition shadow-lg"
                >
                  Explore Courses <ChevronRight className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => navigate("/login")} 
                  className="bg-white text-gray-700 font-bold px-8 py-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition shadow-sm"
                >
                  Join for Free
                </button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=2070&auto=format&fit=crop" 
                alt="Advance Your Career" 
                className="rounded-[40px] shadow-2xl relative z-10 border-[12px] border-white"
              />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: QUOTE SECTION (Image 3 Middle) */}
      <section className="bg-[#FFC107] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <div className="bg-white p-12 md:p-16 rounded-[40px] shadow-xl lg:w-1/2">
              <p className="text-3xl md:text-4xl font-black text-gray-900 leading-tight">
                "Every great career starts with a vision. We teach you how to bring it to life."
              </p>
            </div>
            <div className="lg:w-1/2">
              <img 
                src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2070&auto=format&fit=crop" 
                alt="Vision" 
                className="rounded-[40px] shadow-xl w-full h-[300px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: ADVANCE YOUR CAREER (Image 3 Middle 2) */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left mb-16">
            <h2 className="text-4xl font-black text-[#4299E1] mb-4">Advance Your Career,<br />Learn Something New</h2>
            <p className="text-gray-500 max-w-xl text-lg">
              Our comprehensive guide to delivering the best educational experience.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?q=80&w=2070&auto=format&fit=crop" 
                alt="Student learning" 
                className="rounded-[40px] shadow-2xl w-full"
              />
            </div>
            <div className="space-y-4">
              {[
                { title: "Quality Delivery", text: "We believe in adding the extra mile in delivering quality to exceed the customers' expectations, both expressed and implied. We do it for both external and internal customers." },
                { title: "Responsiveness", text: "We continuously look ways to respond fast to the ever-evolving changing and expanding community." },
                { title: "Dignity & Care", text: "We treat people with dignity, care for our colleagues, collaborate across boundaries and contribute wherever we can." },
                { title: "Professionalism", text: "We are fair in our dealings and follow the highest level of ethical, social, moral, financial and professional conduct." }
              ].map((feature, i) => (
                <div key={i} className="flex gap-4 p-6 bg-[#F8FAFC] rounded-2xl border border-gray-100 hover:shadow-md transition">
                  <div className="shrink-0 w-12 h-12 bg-[#FFC107]/20 rounded-xl flex items-center justify-center">
                    <ShieldCheck className="w-6 h-6 text-[#FFC107]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{feature.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4: ALL LEVELS (Image 3 Middle 3) */}
      <section className="py-24 bg-[#3498DB] relative overflow-hidden">
        {/* Decorative background shapes */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tight">All Levels</h2>
            <div className="h-1 w-20 bg-[#FFC107] mx-auto mt-4 rounded-full" />
            <p className="mt-4 text-white/80 font-inter text-sm max-w-md mx-auto">
              From absolute beginner to certified expert, our curriculum is structured to support your learning journey at every step.
            </p>
          </div>

          <div className="relative">
            {/* Horizontal line behind cards on desktop */}
            <div className="absolute top-12 left-[10%] right-[10%] h-0.5 bg-white/20 hidden lg:block z-0" />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-6 relative z-10">
              {[
                { id: "01", name: "Beginner/Novice", text: "You have knowledge but with no practical experience. Requiring supervision and guidance." },
                { id: "02", name: "Intermediate/Competent", text: "Competent, have solid ability in the area, generating when achieving results from a long-term." },
                { id: "03", name: "Advanced", text: "Can provide strategy proficiently and can work independently, selectively reading complex tasks." },
                { id: "04", name: "Expert", text: "Possess deep knowledge and master skills to build and guide others." },
                { id: "Cert", name: "Certificate", text: "Authorized to recognize all your skills and knowledge." }
              ].map((level, i) => (
                <div 
                  key={level.id} 
                  className="bg-white p-6 rounded-2xl shadow-xl text-center flex flex-col justify-between relative hover:-translate-y-2 transition duration-300 border border-white/20"
                >
                  <div>
                    <div className="w-12 h-12 rounded-full bg-[#3498DB]/10 flex items-center justify-center mx-auto mb-4 font-black text-[#3498DB] text-lg relative z-10 border-4 border-white">
                      {level.id}
                    </div>
                    <h3 className="text-gray-900 font-bold text-base mb-3 tracking-tight leading-snug">
                      {level.name}
                    </h3>
                    <p className="text-gray-500 text-xs leading-relaxed font-inter">
                      {level.text}
                    </p>
                  </div>

                  {/* Connecting yellow dots for desktop */}
                  {i < 4 && (
                    <div className="hidden lg:flex items-center absolute top-12 -right-3.5 -translate-y-1/2 z-20 translate-x-1/2">
                      <div className="w-3.5 h-3.5 bg-[#FFC107] rounded-full ring-4 ring-[#3498DB] shadow-lg animate-pulse" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5: COMPARING SKILLED TO UNSKILLED (Image 3 Middle 4) */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-black text-gray-900 text-center mb-16">Comparing "Skilled" to "Unskilled"</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {/* Skilled Card */}
            <div className="rounded-[30px] overflow-hidden shadow-2xl border border-gray-100">
              <div className="bg-[#3498DB] py-6 text-center">
                <h3 className="text-white font-black text-xl uppercase tracking-widest">Skilled</h3>
              </div>
              <div className="bg-[#F1C40F] p-8 md:p-12 space-y-8">
                <div>
                  <h4 className="font-black text-gray-900 mb-2">Definition:</h4>
                  <p className="text-gray-800 text-sm">Under-running specialized and secondary ability and knowledge possessed for effective performing area tasks.</p>
                </div>
                <div>
                  <h4 className="font-black text-gray-900 mb-2">Key Characteristics:</h4>
                  <ul className="text-gray-800 text-sm space-y-2">
                    <li>• Model advanced knowledge and efficiency.</li>
                    <li>• Complex features, high judgement and problem-solving.</li>
                    <li>• Multi-competent potential.</li>
                    <li>• Greater job security due to specialization.</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-black text-gray-900 mb-2">Benefits:</h4>
                  <ul className="text-gray-800 text-sm space-y-2">
                    <li>• Higher earning potential.</li>
                    <li>• Significant contribution toward success of planning and evaluation activities.</li>
                    <li>• Opportunities for professional growth and advancement.</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Unskilled Card */}
            <div className="rounded-[30px] overflow-hidden shadow-2xl border border-gray-100">
              <div className="bg-[#2C3E50] py-6 text-center">
                <h3 className="text-white font-black text-xl uppercase tracking-widest">Unskilled</h3>
              </div>
              <div className="bg-[#F1C40F] p-8 md:p-12 space-y-8">
                <div>
                  <h4 className="font-black text-gray-900 mb-2">Definition:</h4>
                  <p className="text-gray-800 text-sm">John 11:35 He performed tasks never knowing and requiring little or no training or knowledge.</p>
                </div>
                <div>
                  <h4 className="font-black text-gray-900 mb-2">Key Characteristics:</h4>
                  <ul className="text-gray-800 text-sm space-y-2">
                    <li>• Limited formal education or training.</li>
                    <li>• Routine tasks that can be learned quickly.</li>
                    <li>• Lower earning potential.</li>
                    <li>• Potentially higher job turnover rates.</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-black text-gray-900 mb-2">Benefits:</h4>
                  <ul className="text-gray-800 text-sm space-y-2">
                    <li>• Entry-level positions with minimal barriers to entry.</li>
                    <li>• Potential for on-the-job training and experience over time.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: BOTTOM BANNER (Image 3 Bottom) */}
      <section className="py-24 bg-[#FFC107]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-12">
            Skill Up. Stand Tall.<br />
            Succeed with Alearn India
          </h2>
          <div className="bg-white inline-block px-16 py-6 rounded-2xl shadow-2xl transform hover:scale-105 transition">
            <span className="text-[#0056b3] text-5xl font-black italic tracking-tighter">confidence</span>
          </div>
        </div>
      </section>
    </main>
  );
}
